/**
 * SPA hooks for Keepa query templates — Phase 1.8 Pass 5 wiring.
 *
 * Wraps the /api/keepa-templates routes from Pass 1 with TanStack Query +
 * the typed apiFetch helper. Mirrors the patterns in suppliers / outreach /
 * pricing-sheets hooks for consistency.
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CloneKeepaTemplateRequest,
  CreateKeepaTemplateRequest,
  CreateKeepaTemplateWithScheduleRequest,
  CreateKeepaTemplateWithScheduleResponse,
  KeepaSearchRunResponse,
  KeepaTemplateResponse,
  ListKeepaTemplatesResponse,
  ListTemplateProductsResponse,
  RunKeepaTemplateRequest,
  UpdateKeepaTemplateRequest,
  type ListKeepaTemplatesQuery,
  type ListTemplateProductsQuery,
} from '@sourcito/schemas';
import { apiFetch } from './api';

export type KeepaTemplatesParams = Partial<ListKeepaTemplatesQuery>;

export function keepaTemplatesQuery(params: KeepaTemplatesParams = {}) {
  const search = new URLSearchParams();
  if (params.origin) search.set('origin', params.origin);
  if (params.includeArchived) search.set('includeArchived', 'true');
  const qs = search.toString();
  const path = `/api/keepa-templates${qs ? `?${qs}` : ''}`;

  return queryOptions({
    queryKey: ['keepa-templates', params.origin ?? 'any', params.includeArchived ?? false] as const,
    queryFn: () =>
      apiFetch<ListKeepaTemplatesResponse>(path, {
        responseSchema: ListKeepaTemplatesResponse,
      }),
    staleTime: 60_000,
  });
}

export function useKeepaTemplates(params: KeepaTemplatesParams = {}) {
  return useQuery(keepaTemplatesQuery(params));
}

export function useKeepaTemplate(id: string | null) {
  return useQuery({
    queryKey: ['keepa-templates', 'detail', id] as const,
    queryFn: () =>
      apiFetch<KeepaTemplateResponse>(`/api/keepa-templates/${id}`, {
        responseSchema: KeepaTemplateResponse,
      }),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

export function useCreateKeepaTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateKeepaTemplateRequest) =>
      apiFetch<KeepaTemplateResponse>('/api/keepa-templates', {
        method: 'POST',
        body: JSON.stringify(input),
        responseSchema: KeepaTemplateResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keepa-templates'] });
    },
  });
}

/**
 * Combined create: persists a template AND starts a recurring schedule
 * in a single round trip. Backs the SearchPage preview-card "Save &
 * start hourly" action — see docs/plans/2026-05-23-searchpage-save-schedule-ux-design.md §3.
 *
 * Invalidates both the templates list AND the active-schedules query
 * since either could be reading stale state after the call.
 */
export function useCreateKeepaTemplateWithSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateKeepaTemplateWithScheduleRequest) =>
      apiFetch<CreateKeepaTemplateWithScheduleResponse>('/api/keepa-templates/with-schedule', {
        method: 'POST',
        body: JSON.stringify(input),
        responseSchema: CreateKeepaTemplateWithScheduleResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keepa-templates'] });
      qc.invalidateQueries({ queryKey: ['active-schedules'] });
    },
  });
}

export function useUpdateKeepaTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateKeepaTemplateRequest) =>
      apiFetch<KeepaTemplateResponse>(`/api/keepa-templates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        responseSchema: KeepaTemplateResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keepa-templates'] });
    },
  });
}

export function useCloneKeepaTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input?: CloneKeepaTemplateRequest }) =>
      apiFetch<KeepaTemplateResponse>(`/api/keepa-templates/${id}/clone`, {
        method: 'POST',
        body: JSON.stringify(input ?? {}),
        responseSchema: KeepaTemplateResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keepa-templates'] });
    },
  });
}

export function useArchiveKeepaTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<null>(`/api/keepa-templates/${id}/archive`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keepa-templates'] });
    },
  });
}

// ─── Phase 1.9 Pass 3 — Per-template discovered products ────────────────

export type TemplateProductsParams = Partial<ListTemplateProductsQuery>;

export function templateProductsQuery(
  templateId: string | null,
  params: TemplateProductsParams = {}
) {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.hidePassed !== undefined) search.set('hidePassed', String(params.hidePassed));
  if (params.sortBy) search.set('sortBy', params.sortBy);
  if (params.sortOrder) search.set('sortOrder', params.sortOrder);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  const qs = search.toString();

  return queryOptions({
    queryKey: [
      'keepa-templates',
      'products',
      templateId,
      params.status ?? 'any',
      params.hidePassed ?? true,
      params.sortBy ?? 'recent',
      params.sortOrder ?? 'desc',
      params.limit ?? 100,
    ] as const,
    queryFn: () =>
      apiFetch<ListTemplateProductsResponse>(
        `/api/keepa-templates/${templateId}/products${qs ? `?${qs}` : ''}`,
        { responseSchema: ListTemplateProductsResponse }
      ),
    enabled: Boolean(templateId),
    staleTime: 30_000,
  });
}

export function useTemplateDiscoveredProducts(
  templateId: string | null,
  params: TemplateProductsParams = {}
) {
  return useQuery(templateProductsQuery(templateId, params));
}

export function useRunKeepaTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RunKeepaTemplateRequest }) =>
      apiFetch<KeepaSearchRunResponse>(`/api/keepa-templates/${id}/run`, {
        method: 'POST',
        body: JSON.stringify(input),
        responseSchema: KeepaSearchRunResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keepa-search-runs'] });
      qc.invalidateQueries({ queryKey: ['keepa-templates'] });
    },
  });
}
